import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select } from '@components';
import { CreateAgentData } from '../../types';

interface ConfigurationTabProps {
  formData: CreateAgentData;
  onInputChange: (field: keyof CreateAgentData, value: string | number) => void;
  errors: Record<string, string>;
}

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({ formData, onInputChange, errors }) => {
  const { t } = useTranslation();

  // Noise Reduction options
  const noiseReductionOptions = [
    { value: 'off', label: t('agents.noiseReductionOff') },
    { value: 'near_field', label: t('agents.noiseReductionNearField') },
    { value: 'far_field', label: t('agents.noiseReductionFarField') },
  ];

  const handleBoundedNumberInput =
    (field: keyof CreateAgentData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const valueStr = e.target.value;
      if (valueStr === '') {
        onInputChange(field, '' as unknown as number);
        return;
      }

      const value = parseInt(valueStr, 10);
      if (Number.isNaN(value)) {
        return;
      }

      // Akzeptiere den Wert während der Eingabe ohne Clamping
      onInputChange(field, value);
    };

  const handleBoundedNumberBlur =
    (field: keyof CreateAgentData, fallback: number, minValue?: number, maxValue?: number) =>
    () => {
      const currentValue = formData[field];
      let finalValue: number;

      if (currentValue === '' || currentValue === null || currentValue === undefined) {
        finalValue = fallback;
      } else {
        // Clamping nur beim Blur (Feld verlassen)
        const numValue = currentValue as number;
        finalValue = Math.min(maxValue ?? numValue, Math.max(minValue ?? 0, numValue));
      }

      onInputChange(field, finalValue);
    };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('agents.callsAndConfiguration')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('agents.callsAndConfigurationDescription')}
        </p>
      </div>

      <div className="space-y-8">
        {/* Row 1: Call Duration + AI Behavior */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Call Duration Settings */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                schedule
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.callDuration')}
              </h4>
            </div>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="max-call-time"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('agents.maxCallTimeMinutes')}
                </label>
                <Input
                  id="max-call-time"
                  type="number"
                  className="w-full"
                  value={
                    formData.max_call_time_minutes === null ||
                    formData.max_call_time_minutes === undefined
                      ? ''
                      : formData.max_call_time_minutes
                  }
                  onChange={handleBoundedNumberInput('max_call_time_minutes')}
                  onBlur={handleBoundedNumberBlur('max_call_time_minutes', 30, 1, 120)}
                  min={1}
                  max={120}
                  state={errors.max_call_time_minutes ? 'error' : 'default'}
                  errorText={errors.max_call_time_minutes}
                  rightIcon={<span className="text-sm text-gray-500 dark:text-gray-400">Min</span>}
                />
              </div>

              <div>
                <label
                  htmlFor="max-waiting-time"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('agents.maxWaitingTimeSeconds')}
                </label>
                <Input
                  id="max-waiting-time"
                  type="number"
                  className="w-full"
                  value={
                    formData.max_waiting_time_seconds === null ||
                    formData.max_waiting_time_seconds === undefined
                      ? ''
                      : formData.max_waiting_time_seconds
                  }
                  onChange={handleBoundedNumberInput('max_waiting_time_seconds')}
                  onBlur={handleBoundedNumberBlur('max_waiting_time_seconds', 15, 5, 30)}
                  min={5}
                  max={30}
                  helperText={t('agents.maxWaitingTimeRange')}
                  state={errors.max_waiting_time_seconds ? 'error' : 'default'}
                  errorText={errors.max_waiting_time_seconds}
                  rightIcon={<span className="text-sm text-gray-500 dark:text-gray-400">Sek</span>}
                />
              </div>
            </div>
          </div>

          {/* AI Behavior Settings */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                psychology
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.aiBehavior')}
              </h4>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.responseCreativity')}: {formData.creativity || 68}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={formData.creativity || 68}
                  onChange={e => onInputChange('creativity', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(((formData.creativity || 68) - 0) / (100 - 0)) * 100}%, #e5e7eb ${(((formData.creativity || 68) - 0) / (100 - 0)) * 100}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>{t('agents.conservative')}</span>
                  <span>{t('agents.balanced')}</span>
                  <span>{t('agents.creative')}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.speechDetectionThreshold')}: {formData.threshold || '0.60'}
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  value={formData.threshold || '0.60'}
                  onChange={e => onInputChange('threshold', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((parseFloat(formData.threshold || '0.60') - 0.1) / (1 - 0.1)) * 100}%, #e5e7eb ${((parseFloat(formData.threshold || '0.60') - 0.1) / (1 - 0.1)) * 100}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>0.1 ({t('agents.moreSensitive')})</span>
                  <span>{t('agents.lowerValuesMoreSensitive')}</span>
                  <span>1.0 ({t('agents.lessSensitive')})</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Voice Activity Detection + Timing Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Voice Activity Detection Settings */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                hearing
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.voiceActivityDetection')}
              </h4>
            </div>
            <div className="space-y-4">
              {/* Noise Reduction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.noiseReduction')}
                </label>
                <Select
                  options={noiseReductionOptions}
                  value={formData.noise_reduction_mode || 'off'}
                  onChange={value => onInputChange('noise_reduction_mode', value as string)}
                  helperText={t('agents.noiseReductionDescription')}
                />
              </div>
            </div>
          </div>

          {/* Timing Configuration */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                timer
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.timingConfiguration')}
              </h4>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.prefixPadding')}: {formData.prefix_padding || 400}ms
                </label>
                <input
                  type="range"
                  min="200"
                  max="1000"
                  step="50"
                  value={formData.prefix_padding || 400}
                  onChange={e => onInputChange('prefix_padding', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(((formData.prefix_padding || 400) - 200) / (1000 - 200)) * 100}%, #e5e7eb ${(((formData.prefix_padding || 400) - 200) / (1000 - 200)) * 100}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>200ms</span>
                  <span>{t('agents.bufferTimeBeforeProcessing')}</span>
                  <span>1000ms</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.silenceDetection')}: {formData.silence || 500}ms
                </label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="50"
                  value={formData.silence || 500}
                  onChange={e => onInputChange('silence', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(((formData.silence || 500) - 200) / (2000 - 200)) * 100}%, #e5e7eb ${(((formData.silence || 500) - 200) / (2000 - 200)) * 100}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>200ms</span>
                  <span>{t('agents.durationOfSilenceBeforeEnd')}</span>
                  <span>2000ms</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ConfigurationTab };
